// src/components/admin/PendingVerifications.tsx
// ✅ REDESIGNED: Admin manually enters handle + code from social media DMs

import { useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

type Platform = 'instagram' | 'tiktok' | 'snapchat' | 'facebook' | 'linkedin';

export default function PendingVerifications() {
  const [platform, setPlatform] = useState<Platform>('instagram');
  const [handle, setHandle] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!handle.trim() || !verificationCode.trim()) {
      setResult({ success: false, message: 'Please enter both handle and code' });
      return;
    }

    setVerifying(true);
    setResult(null);

try {
  // Normalize handle (remove @ if present)
  const cleanHandle = handle.trim().replace(/^@/, '');

const response: any = await client.graphql({
  query: /* GraphQL */ `
    mutation VerifyHandle($platform: String!, $handle: String!, $verificationCode: String!, $employeeId: String) {
      employeeVerifyHandle(
        platform: $platform
        handle: $handle
        verificationCode: $verificationCode
        employeeId: $employeeId
      )
    }
  `,
  variables: {
    platform: platform,
    handle: cleanHandle.toLowerCase(),
    verificationCode: verificationCode.trim().toUpperCase(),
    employeeId: 'admin',
  }
});

// The response is a JSON string, parse it
const resultData = JSON.parse(response.data.employeeVerifyHandle);

  if (resultData.success) {
    setResult({ 
      success: true, 
      message: `✅ @${cleanHandle} verified successfully!${
        resultData.matchesRevealed > 0 
          ? ` ${resultData.matchesRevealed} match(es) revealed! 💕`
          : ''
      }`
    });
    // Clear form
    setHandle('');
    setVerificationCode('');
  } else {
    setResult({ 
      success: false, 
      message: resultData.error || '❌ Verification failed' 
    });
  }
} catch (error: any) {
  void ['Verification failed:', error];
  setResult({ 
    success: false, 
    message: '❌ Error: ' + (error.message || 'Unknown error') 
  });
} finally {
  setVerifying(false);
}
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Instructions Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex gap-4">
          <div className="text-4xl">📱</div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              How to Verify Handles
            </h3>
            <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
              <li>Check @crushhh.app DMs on Instagram/Snapchat/TikTok/etc</li>
              <li>User sends their verification code (e.g., "X7K2P9")</li>
              <li>Enter their handle and code below</li>
              <li>Click Verify - backend checks if code matches</li>
              <li>User's handle gets verified! ✅</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Verification Form */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Verify Handle
        </h2>

        <form onSubmit={handleVerify} className="space-y-4">
          {/* Platform Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform
            </label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Platform)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
              disabled={verifying}
            >
              <option value="instagram">Instagram</option>
              <option value="snapchat">Snapchat</option>
              <option value="tiktok">TikTok</option>
              <option value="facebook">Facebook</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>

          {/* Handle Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Handle (from DM)
            </label>
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@username or username"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
              disabled={verifying}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter exactly as it appears in their profile
            </p>
          </div>

          {/* Code Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code (from DM)
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
              placeholder="X7K2P9"
              maxLength={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand font-mono text-lg tracking-wider"
              disabled={verifying}
            />
            <p className="text-xs text-gray-500 mt-1">
              6-character code they sent via DM
            </p>
          </div>

          {/* Result Message */}
          {result && (
            <div className={`rounded-lg p-4 ${
              result.success 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <p className="font-medium">{result.message}</p>
            </div>
          )}

{/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setHandle('');
                setVerificationCode('');
                setResult(null);
              }}
              disabled={verifying}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg transition-colors"
            >
              🔄 Clear
            </button>
            <button
              type="submit"
              disabled={verifying || !handle.trim() || !verificationCode.trim()}
              className="flex-1 px-6 py-3 bg-brand text-white rounded-lg hover:bg-branddark disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg transition-colors"
            >
              {verifying ? 'Verifying...' : '✅ Verify Handle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}